import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Loader2, Monitor, Printer, RotateCcw, Settings2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { SignatureDialog } from '@/components/signature/SignatureDialog'
import { ZplPreview } from '@/components/integrations/printers/ZplPreview'
import { usePrintContext, usePrintLabels } from '@/hooks/useLabelPrinting'
import { useAuth } from '@/providers/AuthProvider'
import { PERMISSIONS, usePermission } from '@/lib/permissions'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { cn } from '@/lib/utils'
import {
  LABEL_TYPE_ROWS,
  printButtonText,
  printSummary,
  zplLabelValues,
  type PrinterFamily,
} from '@/lib/labelPrinting'
import type {
  PrintContextPayload,
  PrintLabelsResult,
  PrintProductChoices,
  PrintSubject,
} from '@/api/labelPrintingApi'
import {
  FactsList,
  FamilyTile,
  LabelDesk,
  LayoutPicker,
  LabelStepper,
  NoIsbtPreview,
  PrinterCard,
  PrintResultPanel,
  ProblemCallout,
  StepHeading,
  WarningNote,
} from './printParts'

/**
 * What a print button asks to print, and how it printed before printers were
 * set up (z8nrz7evkc follow-up).
 */
export interface PrintLabelRequest {
  subject: PrintSubject
  /** Product, lot or equipment ids. A location label is printed from its lot. */
  ids: string[]
  /** Dialog heading, e.g. "Lot LOT-26-0001" or "12 lot labels". */
  title: string
  /** Products only: the Label Printing choices. */
  selections?: PrintProductChoices
  /** Today's browser label, offered as "Print in browser". */
  browser?: {
    print: () => void | Promise<void>
    /** The browser label per id, same order, to show in the Browser tab. */
    previewHtml: string[]
    previewStyle: string
  }
  /**
   * Products only: sign and record the labels before they print, wherever
   * they print. The same password also signs a "sign to print" printer.
   */
  record?: {
    signatureTitle: string
    signatureDescription: ReactNode
    meaning: string
    /** Records the labels; returns the recorded label id of each id, same order. */
    run: (signingToken: string) => Promise<string[]>
  }
}

const FAMILY_NAME: Record<PrinterFamily, string> = { isbt: 'ISBT', zebra: 'Zebra' }
const LAST_PRINTER_KEY = 'cgt.print.lastPrinter.'

function rememberedPrinter(labelType: string): string | null {
  try {
    return localStorage.getItem(LAST_PRINTER_KEY + labelType)
  } catch {
    return null
  }
}

function rememberPrinter(labelType: string, printerId: string) {
  try {
    localStorage.setItem(LAST_PRINTER_KEY + labelType, printerId)
  } catch {
    // A convenience only.
  }
}

type Target = 'printer' | 'browser'

/** "Lot LOT-1" or "3 labels (Lot LOT-1, Lot LOT-2, …)". */
function unansweredLabelText(ids: string[], labels: { id: string; title: string }[]): string {
  const titles = ids.map(id => labels.find(l => l.id === id)?.title ?? id)
  if (titles.length === 1) return titles[0]
  const shown = titles.slice(0, 3).join(', ')
  return `${titles.length} labels (${shown}${titles.length > 3 ? ', …' : ''})`
}

/**
 * The print popup every label print button opens. Printer association decides
 * which kind of printer this label goes to; the user picks one of those
 * printers and one of its approved layouts, checks the label, and prints.
 * The backend fills the label and sends it to the integration service. The
 * browser print stays as a fallback while printers are being set up.
 */
export function PrintLabelDialog({
  request,
  onClose,
  onPrinted,
  hidden = false,
  children,
}: {
  request: PrintLabelRequest | null
  onClose: () => void
  /** Hide the popup without resetting it, while a dialog it opened is on top. */
  hidden?: boolean
  /** After the printer took the job (or the browser printed). */
  onPrinted?: (target: Target, result: PrintLabelsResult | null) => void
  /** Extra status under the preview, e.g. "Label #12 recorded · Verify now". */
  children?: ReactNode
}) {
  const { user } = useAuth()
  const { can } = usePermission()
  const payload = useMemo<PrintContextPayload | null>(
    () => (request ? { subject: request.subject, ids: request.ids, selections: request.selections } : null),
    [request],
  )
  const context = usePrintContext(payload)
  const print = usePrintLabels()

  const [printerId, setPrinterId] = useState<string | null>(null)
  const [templateId, setTemplateId] = useState<string | null>(null)
  const [labelIndex, setLabelIndex] = useState(0)
  const [view, setView] = useState<'label' | 'browser'>('label')
  const [signingFor, setSigningFor] = useState<Target | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<PrintLabelsResult | null>(null)
  const [browserPrinted, setBrowserPrinted] = useState(false)
  // Product labels recorded in this popup and not all printed yet (id -> label
  // id). A retry prints them without recording them again.
  const [recorded, setRecorded] = useState<Record<string, string> | null>(null)
  // After a print that stopped partway: the ids still to send. Null means all.
  const [pendingIds, setPendingIds] = useState<string[] | null>(null)
  // Labels that got no answer and may have printed. Kept across retries (a
  // retry sends only the labels surely not sent) until a full print or a new
  // request, so a later success never hides them.
  const [unansweredIds, setUnansweredIds] = useState<string[]>([])

  const ctx = context.data
  const printers = useMemo(() => ctx?.printers ?? [], [ctx])
  const printer = printers.find(p => p.id === printerId) ?? null
  const template = printer?.templates.find(t => t.id === templateId) ?? null
  const labels = ctx?.labels ?? []
  const label = labels[Math.min(labelIndex, labels.length - 1)] ?? null
  const problems = [...new Set(labels.flatMap(l => l.problems))]
  const sendIds = pendingIds ?? request?.ids ?? []
  const count = sendIds.length
  const typeName = LABEL_TYPE_ROWS.find(r => r.labelType === ctx?.labelType)?.label ?? 'These'

  // A new request starts clean.
  useEffect(() => {
    setPrinterId(null)
    setTemplateId(null)
    setLabelIndex(0)
    setView('label')
    setSigningFor(null)
    setError(null)
    setResult(null)
    setBrowserPrinted(false)
    setRecorded(null)
    setPendingIds(null)
    setUnansweredIds([])
  }, [request])

  // Pick the printer used last for this kind of label, else the first that can print.
  useEffect(() => {
    if (!ctx || printerId) return
    const usable = ctx.printers.filter(p => p.templates.length > 0)
    const last = rememberedPrinter(ctx.labelType)
    const pick = usable.find(p => p.id === last) ?? usable[0]
    if (pick) {
      setPrinterId(pick.id)
      setTemplateId(pick.suggestedTemplateId ?? pick.templates[0]?.id ?? null)
    }
  }, [ctx, printerId])

  function choosePrinter(id: string) {
    const next = printers.find(p => p.id === id)
    if (!next) return
    setPrinterId(id)
    // Keep the layout when the new printer has it too.
    setTemplateId(
      next.templates.some(t => t.id === templateId)
        ? templateId
        : (next.suggestedTemplateId ?? next.templates[0]?.id ?? null),
    )
    setResult(null)
    setError(null)
  }

  const printerSigns = !!printer?.signatureRequired
  const canPrint = !!printer && !!template && problems.length === 0 && !busy && !context.isFetching

  const needsRecord = !!request?.record && !recorded

  function start(target: Target) {
    setError(null)
    const needsSignature = needsRecord || (target === 'printer' && printerSigns)
    if (needsSignature) setSigningFor(target)
    else void run(target, [])
  }

  async function run(target: Target, tokens: string[]) {
    if (!request) return
    const queue = [...tokens]
    setBusy(true)
    try {
      let labelIds = recorded
      if (request.record && !labelIds) {
        const ids = await request.record.run(queue.shift()!)
        labelIds = Object.fromEntries(request.ids.map((id, i) => [id, ids[i]]))
        setRecorded(labelIds)
      }
      if (target === 'browser') {
        setSigningFor(null)
        await request.browser?.print()
        setBrowserPrinted(true)
        setRecorded(null)
        setPendingIds(null)
        onPrinted?.('browser', null)
        return
      }
      if (!printer || !template || !ctx) return
      const sent = await print.mutateAsync({
        subject: request.subject,
        ids: sendIds,
        labelIds: labelIds ? sendIds.map(id => labelIds[id]) : undefined,
        selections: request.selections,
        printerId: printer.id,
        templateId: template.id,
        signingToken: printerSigns ? queue.shift() : undefined,
        signatureMeaning: printerSigns ? (request.record?.meaning ?? 'I confirm these labels are correct and approve printing them') : undefined,
      })
      setSigningFor(null)
      setResult(sent)
      // A label with no answer in time may have printed, so it is never sent
      // again under the same record: only labels surely not sent are retried.
      // Printing it again later is a new print, recorded as a reprint.
      const notSent = sent.results
        .filter(r => r.outcome !== 'accepted' && r.outcome !== 'timeout')
        .map(r => r.id)
      // A retry carries the earlier unanswered labels; a full print starts over.
      const timedOut = sent.results.filter(r => r.outcome === 'timeout').map(r => r.id)
      const unanswered = [...new Set([...(pendingIds ? unansweredIds : []), ...timedOut])]
      setUnansweredIds(unanswered)
      if (notSent.length === 0) {
        // All printed (or maybe printed): printing again records a reprint.
        setRecorded(null)
        setPendingIds(null)
        if (unanswered.length === 0) rememberPrinter(ctx.labelType, printer.id)
      } else {
        setPendingIds(notSent)
      }
      onPrinted?.('printer', sent)
    } catch (e) {
      setError(apiErrorMessage(e, 'The label could not be printed.'))
      // The printer, its layouts or its signature rule may have changed.
      void context.refetch()
    } finally {
      setBusy(false)
    }
  }

  const summary = result ? printSummary(result.printer.displayName, result.results) : null
  const sentOk = summary?.tone === 'ok'
  const browser = request?.browser
  const settingsLink = ctx ? `/integrations?tab=printers&printers=${ctx.printerFamily}-printers` : '/integrations?tab=printers'

  return (
    <>
      <Dialog open={!!request && !signingFor && !hidden} onOpenChange={o => !o && !busy && onClose()}>
        <DialogContent className='flex max-h-[90vh] flex-col gap-0 p-0 sm:max-w-5xl'>
          <DialogHeader className='shrink-0 space-y-1 border-b px-6 py-4 text-left'>
            <div className='flex items-center gap-3'>
              {ctx ? <FamilyTile family={ctx.printerFamily} className='h-10 w-10' /> : <FamilyTile family='zebra' className='h-10 w-10 opacity-40' />}
              <div className='min-w-0'>
                <DialogTitle className='truncate'>{request?.title ?? 'Print label'}</DialogTitle>
                <DialogDescription>
                  {ctx
                    ? `${typeName} labels go to ${FAMILY_NAME[ctx.printerFamily]} printers. Set in Printer association.`
                    : 'Finding the printers for this label…'}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className='grid min-h-0 flex-1 overflow-y-auto md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]'>
            {/* Left: where it prints. */}
            <section className='space-y-5 border-b p-5 md:border-b-0 md:border-r'>
              <div>
                <StepHeading
                  step={1}
                  title='Printer'
                  aside={ctx && `${printers.length} ${FAMILY_NAME[ctx.printerFamily]} printer${printers.length === 1 ? '' : 's'}`}
                />
                {context.isLoading ? (
                  <div className='space-y-2'>
                    {[0, 1].map(i => (
                      <div key={i} className='h-[86px] animate-pulse rounded-xl bg-muted' />
                    ))}
                  </div>
                ) : context.isError ? (
                  <p role='alert' className='rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-sm'>
                    {apiErrorMessage(context.error, 'The printers could not be loaded.')}
                  </p>
                ) : printers.length === 0 ? (
                  <div className='flex flex-col items-center gap-2 rounded-xl border border-dashed px-4 py-8 text-center'>
                    <Printer className='h-7 w-7 text-muted-foreground' aria-hidden />
                    <p className='text-sm font-medium'>No {ctx ? FAMILY_NAME[ctx.printerFamily] : ''} printers yet</p>
                    <p className='max-w-xs text-xs text-muted-foreground'>
                      {can(PERMISSIONS.labelPrintingManage)
                        ? 'Add one in Integrations, Printers.'
                        : 'Ask an admin to add one in Integrations, Printers.'}
                      {browser && ' You can print in the browser meanwhile.'}
                    </p>
                    {can(PERMISSIONS.labelPrintingManage) && (
                      <Button asChild size='sm' variant='outline' className='mt-1'>
                        <Link to={settingsLink}>
                          <Settings2 className='mr-1.5 h-3.5 w-3.5' />
                          Printer settings
                        </Link>
                      </Button>
                    )}
                  </div>
                ) : (
                  <div role='radiogroup' aria-label='Printer' className='space-y-2'>
                    {printers.map(p => (
                      <PrinterCard key={p.id} printer={p} selected={p.id === printerId} onSelect={() => choosePrinter(p.id)} />
                    ))}
                  </div>
                )}
              </div>

              {printer && printer.templates.length > 0 && (
                <div>
                  <StepHeading step={2} title='Layout' aside={template?.size ?? undefined} />
                  <LayoutPicker templates={printer.templates} value={templateId} onChange={setTemplateId} />
                </div>
              )}
            </section>

            {/* Right: what it prints. */}
            <section className='space-y-4 p-5'>
              <StepHeading
                step={3}
                title='Check the label'
                aside={
                  browser && (
                    <span role='tablist' aria-label='Preview' className='inline-flex rounded-md border p-0.5'>
                      {(['label', 'browser'] as const).map(v => (
                        <button
                          key={v}
                          type='button'
                          role='tab'
                          aria-selected={view === v}
                          onClick={() => setView(v)}
                          className={cn(
                            'rounded px-2 py-0.5 text-xs font-medium',
                            view === v ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground',
                          )}
                        >
                          {v === 'label' ? (ctx ? FAMILY_NAME[ctx.printerFamily] : 'Printer') : 'Browser'}
                        </button>
                      ))}
                    </span>
                  )
                }
              />

              {summary && result && <PrintResultPanel summary={summary} results={result.results} />}
              {pendingIds && result && (
                <p role='status' className='rounded-lg border bg-muted/40 px-3 py-2 text-sm'>
                  Printing again sends only the {pendingIds.length === 1 ? 'label' : `${pendingIds.length} labels`} not
                  sent.{recorded && (pendingIds.length === 1 ? ' It is already recorded.' : ' They are already recorded.')}
                </p>
              )}
              {unansweredIds.length > 0 && result && (
                <p role='status' className='rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200'>
                  No answer for {unansweredLabelText(unansweredIds, labels)}. Check the printer.
                  {pendingIds ? ' Printing again does not send them.' : ' Printing again prints them as a reprint.'}
                </p>
              )}
              {browserPrinted && !result && (
                <p role='status' className='rounded-lg border bg-muted/40 px-3 py-2 text-sm'>
                  Sent to the browser's print dialog.
                </p>
              )}
              {error && !signingFor && (
                <p role='alert' className='rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive'>
                  {error}
                </p>
              )}

              <LabelStepper labels={labels} index={labelIndex} onChange={setLabelIndex} />

              <LabelDesk>
                {view === 'browser' && browser ? (
                  <>
                    <style>{browser.previewStyle.replace(/^body\{[^}]*\}/, '')}</style>
                    <div
                      className='flex justify-center [&_.lbl]:shadow-sm'
                      dangerouslySetInnerHTML={{ __html: browser.previewHtml[labelIndex] ?? browser.previewHtml[0] ?? '' }}
                    />
                    <p className='mt-2 text-center text-xs text-muted-foreground'>The label the browser prints.</p>
                  </>
                ) : context.isLoading ? (
                  <div className='flex aspect-[2.6] items-center justify-center text-sm text-muted-foreground'>
                    <Loader2 className='mr-2 h-4 w-4 animate-spin' /> Loading the label…
                  </div>
                ) : ctx?.printerFamily === 'zebra' && template?.body && template.size && template.resolution && label ? (
                  <ZplPreview
                    key={`${template.id}-${label.id}`}
                    body={template.body}
                    labelStock={template.size}
                    resolution={template.resolution}
                    placeholders={zplLabelValues(label.zebraValues)}
                    caption={`Drawn with this label's values on ${template.size} at ${template.resolution}. The printed label may differ slightly.`}
                  />
                ) : ctx?.printerFamily === 'isbt' ? (
                  <NoIsbtPreview template={template} />
                ) : (
                  <p className='py-8 text-center text-sm text-muted-foreground'>Pick a printer and a layout to see the label.</p>
                )}
              </LabelDesk>

              {label && <ProblemCallout problems={label.problems} hasBrowser={!!browser} />}
              {label && <WarningNote warnings={label.warnings ?? []} />}
              {label && <FactsList facts={label.facts} />}
              {children}
            </section>
          </div>

          <DialogFooter className='shrink-0 flex-col-reverse gap-2 border-t px-6 py-3 sm:flex-row sm:items-center sm:justify-between sm:space-x-0'>
            <div>
              {browser && (
                <Button variant='ghost' onClick={() => start('browser')} disabled={busy}>
                  <Monitor className='mr-2 h-4 w-4' />
                  Print in browser
                </Button>
              )}
            </div>
            <div className='flex flex-col-reverse gap-2 sm:flex-row'>
              <Button variant='outline' onClick={onClose} disabled={busy}>
                {result || browserPrinted ? 'Done' : 'Cancel'}
              </Button>
              <Button onClick={() => start('printer')} disabled={!canPrint} className='min-w-[11rem]'>
                {busy ? (
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                ) : sentOk ? (
                  <RotateCcw className='mr-2 h-4 w-4' />
                ) : (
                  <Printer className='mr-2 h-4 w-4' />
                )}
                {busy ? 'Sending…' : sentOk ? 'Print again' : printButtonText(count, printerSigns || needsRecord)}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SignatureDialog
        open={!!request && !!signingFor}
        onOpenChange={o => {
          if (!o && !busy) setSigningFor(null)
        }}
        title={(needsRecord && request?.record?.signatureTitle) || 'Sign to print'}
        description={
          (needsRecord && request?.record?.signatureDescription) || (
            <>
              <strong>{printer?.displayName}</strong> needs a signature. Sign that{' '}
              {count === 1 ? 'this label is' : `these ${count} labels are`} correct.
            </>
          )
        }
        confirmLabel={signingFor === 'browser' ? 'Sign & print in browser' : 'Sign & print'}
        signerName={user?.fullName || user?.email}
        busy={busy}
        error={error}
        extraTokens={needsRecord && signingFor === 'printer' && printerSigns ? 1 : 0}
        onConfirm={(token, _signer, extra) => void run(signingFor ?? 'printer', [token, ...extra])}
      />
    </>
  )
}
